// src/components/EntryModal.js
import React, { useState, useEffect, useMemo } from 'react';

// Normalize a pay amount string so "25", "25.0" and "25.00" compare as equal
const normalizeAmount = (v) => {
  const s = (v ?? '').toString().trim();
  if (s === '') return '';
  const n = parseFloat(s);
  return isNaN(n) ? s : n.toFixed(2);
};

export default function EntryModal({
  mode,
  entry,
  associates,
  isOverride,
  isHistorical,    // ← new prop for historical read-only mode
  onSave,
  onDelete,
  onClose,
  saving,
}) {
  const [associateId, setAssociateId] = useState(entry?.guidAssociateID || '');
  const [payAmount,   setPayAmount]   = useState(entry?.dblPayAmount?.toString() || '');
  const [comments,    setComments]    = useState(entry?.strComments || '');
  const [errors,      setErrors]      = useState({});
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  useEffect(() => {
    if (entry) {
      setAssociateId(entry.guidAssociateID || '');
      setPayAmount(entry.dblPayAmount?.toString() || '');
      setComments(entry.strComments || '');
    }
  }, [entry]);

  const validate = () => {
    const errs = {};
    if (mode === 'new' && !associateId) errs.associate = 'Please select an associate.';
    if (!payAmount || isNaN(parseFloat(payAmount)) || parseFloat(payAmount) <= 0)
      errs.payAmount = 'Please enter a valid pay amount greater than 0.';
    return errs;
  };

  const handleSave = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave({
      guidAssociateID: associateId,
      dblPayAmount:    parseFloat(parseFloat(payAmount).toFixed(2)),
      strComments:     comments.trim() || null,
    });
  };

  const handleDelete = () => {
    if (!confirmDel) { setConfirmDel(true); return; }
    onDelete(entry.guidLeadPayHeaderID);
  };

  const isSubmitted = entry?.intStatus === 2;

  // Read-only if submitted OR viewing historical data
  const isReadOnly = isSubmitted || isHistorical;

  // ── Unsaved-change detection ────────────────────────────────────────────────
  // Values the popup opened with: blank for a new entry, saved values for an edit.
  const initial = useMemo(() => ({
    associateId: entry?.guidAssociateID || '',
    payAmount:   normalizeAmount(entry?.dblPayAmount),
    comments:    (entry?.strComments || '').trim(),
  }), [entry]);

  const isDirty = !isReadOnly && (
    associateId                  !== initial.associateId ||
    normalizeAmount(payAmount)   !== initial.payAmount   ||
    comments.trim()              !== initial.comments
  );

  // Browser "Leave site?" warning while there are unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';   // required for Chrome/Edge to show the prompt
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Cancel / × — ask first if anything changed
  const requestClose = () => {
    if (saving) return;
    if (isDirty) { setConfirmDel(false); setConfirmClose(true); return; }
    onClose();
  };

  const handleSaveFromPrompt = () => {
    setConfirmClose(false);   // hide the prompt so any validation errors are visible
    handleSave();
  };

  return (
    // Background click intentionally does nothing — the popup must be closed
    // with Save, Cancel, × or Delete so changes are never lost by accident.
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h3>
            {isHistorical
              ? '📋 Historical Entry (Read Only)'
              : mode === 'new'
                ? '+ New Lead Pay Entry'
                : 'Edit Lead Pay Entry'}
          </h3>
          <button className="modal-close" onClick={requestClose} disabled={saving}>×</button>
        </div>

        <div className="modal-body">

          {/* Historical banner */}
          {isHistorical && (
            <div className="alert alert-warning">
              📋 This entry is from a closed pay period and cannot be modified.
            </div>
          )}

          {/* Submitted banner — only show if not historical */}
          {!isHistorical && isSubmitted && (
            <div className="alert alert-warning">
              This entry has been submitted and cannot be modified.
            </div>
          )}

          {/* Informational message for default associate list — new mode, not historical */}
          {mode === 'new' && !isOverride && !isHistorical && (
            <div style={{
              background:   'var(--gray-50)',
              border:       '1px solid var(--gray-200)',
              borderLeft:   '4px solid var(--navy-light)',
              borderRadius: 'var(--radius)',
              padding:      '.85rem 1rem',
              fontSize:     '.82rem',
              color:        'var(--gray-600)',
              lineHeight:   1.5
            }}>
              ℹ Regular mode - showing only associates with activity in the selected shift and date.
              If you'd like to override this to enter lead pay for an associate 
              not shown below, go back and click the <strong>"+ New Entry (Override)"</strong> button 
              from the previous screen.
            </div>
          )}

          {/* Override warning banner */}
          {isOverride && mode === 'new' && !isHistorical && (
            <div className="alert alert-warning">
              ⚠ Override mode — showing all lead pay eligible associates homed in this department regardless of hours worked.
            </div>
          )}

          {/* Associate selector (new only, not historical) */}
          {mode === 'new' && !isHistorical && (
            <div className="form-group">
              <label className="form-label">Associate</label>
              <select
                className={`form-control${errors.associate ? ' error' : ''}`}
                value={associateId}
                onChange={e => setAssociateId(e.target.value)}
                disabled={isReadOnly}
              >
                <option value="">— Select Associate —</option>
                {associates.map(a => (
                  <option key={a.guidAssociateID} value={a.guidAssociateID}>
                    {a.displayName ?? `${a.strLastName}, ${a.strFirstName}`}
                  </option>
                ))}
              </select>
              {errors.associate && <span className="form-error">{errors.associate}</span>}
            </div>
          )}

          {/* Edit mode or historical: show name read-only */}
          {(mode === 'edit' || isHistorical) && (
            <div className="form-group">
              <label className="form-label">Associate</label>
              <input
                className="form-control"
                value={entry?.leadName || ''}
                disabled
              />
            </div>
          )}

          {/* Pay Amount */}
          <div className="form-group">
            <label className="form-label">Pay Amount ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={`form-control${errors.payAmount ? ' error' : ''}`}
              value={payAmount}
              onChange={e => setPayAmount(e.target.value)}
              placeholder="0.00"
              disabled={isReadOnly}
            />
            {errors.payAmount && <span className="form-error">{errors.payAmount}</span>}
          </div>

          {/* Comments */}
          <div className="form-group">
            <label className="form-label">Comments <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>(optional)</span></label>
            <textarea
              className="form-control"
              value={comments}
              onChange={e => setComments(e.target.value)}
              rows={3}
              maxLength={200}
              placeholder="Add any relevant notes…"
              disabled={isReadOnly}
              style={{ resize: 'vertical' }}
            />
            <span style={{ fontSize: '.72rem', color: 'var(--gray-400)', textAlign: 'right' }}>
              {comments.length}/200
            </span>
          </div>
        </div>

        {/* Unsaved-changes prompt — replaces the normal footer */}
        {confirmClose ? (
        <div className="modal-footer" style={{ flexWrap: 'wrap' }}>
          <div className="alert alert-warning" style={{ width: '100%', margin: '0 0 .5rem 0' }}>
            ⚠ You have unsaved changes. Do you want to save them before closing?
          </div>
          <button
            className="btn btn-danger btn-sm"
            onClick={onClose}
            disabled={saving}
            style={{ marginRight: 'auto' }}
          >
            Discard Changes
          </button>
          <button
            className="btn btn-outline"
            onClick={() => setConfirmClose(false)}
            disabled={saving}
          >
            Keep Editing
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSaveFromPrompt}
            disabled={saving}
          >
            {saving ? 'Saving…' : '✓ Save Entry'}
          </button>
        </div>
        ) : (
        <div className="modal-footer">
          {/* Delete button — edit mode, not read-only */}
          {mode === 'edit' && !isReadOnly && (
            <button
              className="btn btn-danger btn-sm"
              onClick={handleDelete}
              disabled={saving}
              style={{ marginRight: 'auto' }}
            >
              {confirmDel ? '⚠ Confirm Delete' : '🗑 Delete'}
            </button>
          )}
          {confirmDel && (
            <button
              className="btn btn-outline btn-sm"
              onClick={() => setConfirmDel(false)}
            >
              Cancel
            </button>
          )}

          <button className="btn btn-outline" onClick={requestClose} disabled={saving}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </button>

          {/* Save button — only shown when not read-only */}
          {!isReadOnly && (
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving…' : '✓ Save Entry'}
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
}